import { GQL_ID, PageInfo, Product } from '@@types'
import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { useAuth } from '@features/auth'

import { SHORTCUT_PRODUCT_CARD_FIELDS, SHORTCUT_PRODUCT_FIELDS, SHORTCUT_PRODUCT_SHOP_INFO } from '@shared/api/shortcuts'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'

export interface PreviouslyOrderedProductsData {
  previouslyOrderedProducts: {
    edges: Product[]
    pageInfo: PageInfo
  }
}

export interface PreviouslyOrderedProductsVariables {
  shop_id: GQL_ID
  style?: string
  page?: number
  limit?: number
}

const GQL_QUERY_PREVIOUSLY_ORDERED_PRODUCTS = gql`
  query previouslyOrderedProducts ($shop_id: ID!, $style: String, $page: Int, $limit: Int) {
    previouslyOrderedProducts(
      shop_id: $shop_id
      _page: $page
      _limit: $limit
    ) {
      edges {
        ${SHORTCUT_PRODUCT_FIELDS}
        ${SHORTCUT_PRODUCT_SHOP_INFO}
        ${SHORTCUT_PRODUCT_CARD_FIELDS}
      }
      pageInfo {
        page
        lastPage
        perPage
        total        
      }
    }
  }
`

export const usePreviouslyOrderedProducts = (variables: PreviouslyOrderedProductsVariables) => {

  const { isAuthorized } = useAuth()

  const { data, loading, error, refetch, fetchMore } = useQuery<PreviouslyOrderedProductsData, PreviouslyOrderedProductsVariables>(
    GQL_QUERY_PREVIOUSLY_ORDERED_PRODUCTS,
    {
      variables,
      skip: !variables.shop_id || !isAuthorized
    }
  )

  const previouslyOrderedProducts = useMemo(() => data?.previouslyOrderedProducts?.edges ?? [], [ data ])
  const pageInfo = useMemo(() => data?.previouslyOrderedProducts?.pageInfo, [ data ])

  const { hasMore, loadingMore, fetchNextPage } =
    useFetchMorePagination<
      PreviouslyOrderedProductsData,
      PreviouslyOrderedProductsVariables
    >({
      loading,
      fetchMore,
      pageVariable: 'page',
      usePager: false,
      variables: {
        shop_id: variables.shop_id,
        limit: variables.limit
      },
      updaterFunction (cached, fetched) {
        const prev = cached?.previouslyOrderedProducts?.edges ?? []
        const curr = fetched?.previouslyOrderedProducts?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            previouslyOrderedProducts: {
              ...cached.previouslyOrderedProducts,
              edges: [ ...prev, ...curr ],
            },
          },
        }
      },
    })

  return {
    data,
    previouslyOrderedProducts,
    pageInfo,
    loading,
    error,
    refetch,
    hasMore,
    loadingMore,
    fetchNextPage,
    fetchMore
  }
}
